import { useState } from "react";
import { Plus, Search, Shield, UserCheck } from "lucide-react";
import { DataTable, Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { validateEmail, validateMaxLength } from "../../utils/validators";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  accessLevel: string;
  status: "active" | "inactive" | "pending";
  lastLogin: string;
  mfa: boolean;
}

/* ── Platform roles per REQ-6.1 ─────────────────────────── */
const users: User[] = [
  {
    id: "u1",
    name: "Alex Johnson",
    email: "alex.johnson@org.gov",
    role: "ISSO",
    accessLevel: "Admin",
    status: "active",
    lastLogin: "2026-03-09 14:32",
    mfa: true,
  },
  {
    id: "u2",
    name: "Sarah Kim",
    email: "sarah.kim@org.gov",
    role: "ISSO",
    accessLevel: "Editor",
    status: "active",
    lastLogin: "2026-03-09 11:15",
    mfa: true,
  },
  {
    id: "u3",
    name: "Marcus Davis",
    email: "marcus.davis@org.gov",
    role: "System Owner",
    accessLevel: "Editor",
    status: "active",
    lastLogin: "2026-03-08 17:45",
    mfa: true,
  },
  {
    id: "u4",
    name: "Dr. Christine Moore",
    email: "c.moore@org.gov",
    role: "Authorizing Official (AO)",
    accessLevel: "Approver",
    status: "active",
    lastLogin: "2026-03-07 09:00",
    mfa: true,
  },
  {
    id: "u5",
    name: "James Liu",
    email: "j.liu@org.gov",
    role: "Auditor / IG",
    accessLevel: "Viewer",
    status: "active",
    lastLogin: "2026-03-09 08:30",
    mfa: false,
  },
  {
    id: "u6",
    name: "Patricia Brown",
    email: "p.brown@org.gov",
    role: "Tenant Admin",
    accessLevel: "Admin",
    status: "active",
    lastLogin: "2026-03-06 16:00",
    mfa: true,
  },
  {
    id: "u7",
    name: "David Carter",
    email: "d.carter@org.gov",
    role: "System Owner",
    accessLevel: "Viewer",
    status: "inactive",
    lastLogin: "2026-02-15 10:00",
    mfa: false,
  },
  {
    id: "u8",
    name: "Emily Chen",
    email: "e.chen@coalfire.com",
    role: "Assessor / 3PAO",
    accessLevel: "Assessor",
    status: "pending",
    lastLogin: "Never",
    mfa: false,
  },
];

/* ── RBAC roles per REQ-6.1 - OSCAL Platform roles ─────── */
const roles = [
  {
    id: "r1",
    name: "ISSO",
    description:
      "Maintain SSP, manage POA&M, respond to findings, initiate assessments, attest control implementations",
    users: 2,
    permissions: ["read", "write", "attest", "poam-manage"],
  },
  {
    id: "r2",
    name: "Authorizing Official (AO)",
    description:
      "Grant/deny ATOs, review authorization packages, approve risk acceptances, sign ATOs",
    users: 1,
    permissions: ["read", "approve", "ato-sign"],
  },
  {
    id: "r3",
    name: "Assessor / 3PAO",
    description:
      "Execute assessment plans, ingest findings, create observations and risks, submit assessment results",
    users: 1,
    permissions: ["read", "write-findings", "write-observations"],
  },
  {
    id: "r4",
    name: "Tenant Admin",
    description:
      "Manage users, RBAC, org structure, delegation chains, workflow templates, export profiles",
    users: 1,
    permissions: ["read", "write", "delete", "admin", "user-manage"],
  },
  {
    id: "r5",
    name: "Auditor / IG",
    description:
      "View all artifacts, inspect history tables, export audit logs, access point-in-time snapshots",
    users: 1,
    permissions: ["read", "audit-export"],
  },
  {
    id: "r6",
    name: "System Owner",
    description:
      "View compliance posture, approve risk acceptances, review authorization packages, set system metadata",
    users: 2,
    permissions: ["read", "risk-accept", "metadata-edit"],
  },
];

const userColumns: Column<User>[] = [
  {
    key: "name",
    header: "User",
    align: "left",
    render: (row) => (
      <div className="flex items-center gap-3">
        <div className="size-7 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-medium flex-shrink-0">
          {row.name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)}
        </div>
        <div>
          <p className="text-sm font-medium">{row.name}</p>
          <p className="text-xs d">{row.email}</p>
        </div>
      </div>
    ),
  },
  {
    key: "role",
    header: "Role",
    align: "left",
    render: (row) => <>{row.role}</>,
  },
  {
    key: "accessLevel",
    header: "Access Level",
    align: "center",
    render: (row) => (
      <span className="text-xs px-2 py-0.5 rounded bg-muted d font-medium">
        {row.accessLevel}
      </span>
    ),
  },
  {
    key: "mfa",
    header: "MFA",
    align: "center",
    render: (row) => (
      <span
        className={`text-xs font-medium ${row.mfa ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}
      >
        {row.mfa ? "✓ Enabled" : "✗ Disabled"}
      </span>
    ),
  },
  {
    key: "lastLogin",
    header: "Last Login",
    align: "center",
    render: (row) => <>{row.lastLogin}</>,
  },
  {
    key: "status",
    header: "Status",
    align: "center",
    render: (row) => <StatusBadge status={row.status} dot />,
  },
];

export function Users() {
  const [activeTab, setActiveTab] = useState<"users" | "roles">("users");
  const [search, setSearch] = useState("");

  // Modals
  const [showInvite, setShowInvite] = useState(false);
  const [showNewRole, setShowNewRole] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    role: "",
    accessLevel: "",
  });
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
    permissions: [] as string[],
  });
  const [roleErrors, setRoleErrors] = useState<Record<string, string>>({});

  const [userSortKey, setUserSortKey] = useState<string | null>(null);
  const [userSortDir, setUserSortDir] = useState<"asc" | "desc">("asc");
  const handleUserSort = (key: string) => {
    if (userSortKey === key)
      setUserSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setUserSortKey(key);
      setUserSortDir("asc");
    }
  };
  const UserSortIcon = ({ col }: { col: string }) => {
    const active = userSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && userSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && userSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const filtered = users.filter(
    (u) =>
      !search ||
      [u.name, u.email, u.role].some((v) =>
        v.toLowerCase().includes(search.toLowerCase()),
      ),
  );

  const sortedUsers = [...filtered].sort((a, b) => {
    if (!userSortKey) return 0;
    const aVal: any = a[userSortKey as keyof User];
    const bVal: any = b[userSortKey as keyof User];
    if (typeof aVal === "boolean")
      return userSortDir === "asc"
        ? aVal === bVal
          ? 0
          : aVal
            ? -1
            : 1
        : aVal === bVal
          ? 0
          : aVal
            ? 1
            : -1;
    return userSortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const sortedUserColumns: Column<User>[] = [
    {
      key: "name",
      header: (
        <button
          onClick={() => handleUserSort("name")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          User
          <UserSortIcon col="name" />
        </button>
      ),
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="size-7 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-medium flex-shrink-0">
            {row.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </div>
          <div>
            <p className="text-sm font-medium">{row.name}</p>
            <p className="text-xs d">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role",
      header: (
        <button
          onClick={() => handleUserSort("role")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Role
          <UserSortIcon col="role" />
        </button>
      ),
      align: "left",
      render: (row) => <>{row.role}</>,
    },
    {
      key: "accessLevel",
      header: (
        <button
          onClick={() => handleUserSort("accessLevel")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Access Level
          <UserSortIcon col="accessLevel" />
        </button>
      ),
      align: "center",
      render: (row) => (
        <span className="text-xs px-2 py-0.5 rounded bg-muted d font-medium">
          {row.accessLevel}
        </span>
      ),
    },
    {
      key: "mfa",
      header: "MFA",
      align: "center",
      render: (row) => (
        <span
          className={`text-xs font-medium ${row.mfa ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}
        >
          {row.mfa ? "✓ Enabled" : "✗ Disabled"}
        </span>
      ),
    },
    {
      key: "lastLogin",
      header: (
        <button
          onClick={() => handleUserSort("lastLogin")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Last Login
          <UserSortIcon col="lastLogin" />
        </button>
      ),
      align: "center",
      render: (row) => <>{row.lastLogin}</>,
    },
    {
      key: "status",
      header: (
        <button
          onClick={() => handleUserSort("status")}
          className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
        >
          Status
          <UserSortIcon col="status" />
        </button>
      ),
      align: "center",
      render: (row) => <StatusBadge status={row.status} dot />,
    },
  ];

  const mfaEnabled = users.filter((u) => u.mfa && u.status === "active").length;
  const activeCount = users.filter((u) => u.status === "active").length;

  const addButtonConfig = {
    users: { label: "Invite User", action: () => setShowInvite(true) },
    roles: { label: "New Role", action: () => setShowNewRole(true) },
  };
  const addBtn = addButtonConfig[activeTab];

  const allPermissions = [
    "read",
    "write",
    "delete",
    "admin",
    "approve",
    "write-findings",
    "write-events",
  ];

  // Validation functions
  const validateInviteForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate name
    if (!inviteForm.name.trim()) {
      errors.name = "Full name is required";
    } else if (inviteForm.name.length > 255) {
      errors.name = "Full name must not exceed 255 characters";
    }

    // Validate email
    const emailValidation = validateEmail(inviteForm.email);
    if (!emailValidation.valid) {
      errors.email = emailValidation.error || "Invalid email";
    }

    // Validate access level
    if (!inviteForm.accessLevel) {
      errors.accessLevel = "Access level is required";
    }

    setInviteErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRoleForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!roleForm.name.trim()) {
      errors.name = "Role name is required";
    } else if (roleForm.name.length > 255) {
      errors.name = "Role name must not exceed 255 characters";
    }

    if (roleForm.permissions.length === 0) {
      errors.permissions = "At least one permission is required";
    }

    setRoleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>User Management</h1>
          <p className="text-sm d mt-0.5">
            Manage platform users and role-based access control
          </p>
        </div>
        <button
          onClick={addBtn.action}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Plus className="size-4" />
          {addBtn.label}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Users", value: users.length, icon: UserCheck },
          { label: "Active", value: activeCount, icon: UserCheck },
          {
            label: "MFA Enabled",
            value: `${mfaEnabled}/${activeCount}`,
            icon: Shield,
          },
          {
            label: "Pending Invite",
            value: users.filter((u) => u.status === "pending").length,
            icon: UserCheck,
          },
        ].map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="rounded-lg border border-border bg-card p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs d">{label}</p>
              <Icon className="size-3.5 d" />
            </div>
            <p className="text-xl font-semibold text-foreground">{value}</p>
          </div>
        ))}
      </div>

      {/* MFA warning */}
      {users.some((u) => u.status === "active" && !u.mfa) && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/5">
          <Shield className="size-4 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {users.filter((u) => u.status === "active" && !u.mfa).length} active
            user(s) do not have MFA enabled.
            <button className="ml-2 underline hover:no-underline">
              Enforce MFA Policy
            </button>
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-border">
        {(["users", "roles"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? "border-foreground text-foreground"
                : "border-transparent d hover:text-foreground"
            }`}
          >
            {tab === "users" ? "Users" : "Roles & Permissions"}
          </button>
        ))}
      </div>

      {activeTab === "users" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-muted/30 flex-1 max-w-xs">
              <Search className="size-3.5 d flex-shrink-0" />
              <input
                type="text"
                placeholder="Search users"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none min-w-0"
              />
            </div>
          </div>
          <DataTable
            columns={sortedUserColumns}
            data={sortedUsers}
            rowKey={(r) => r.id}
          />
        </div>
      )}

      {activeTab === "roles" && (
        <div className="space-y-3">
          {roles.map((role) => (
            <div
              key={role.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="size-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <Shield className="size-4 d" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{role.name}</p>
                    <p className="text-xs d mt-0.5">{role.description}</p>
                    <p className="text-xs d mt-1">
                      {role.users} user{role.users !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {role.permissions.map((perm) => (
                    <span
                      key={perm}
                      className="text-xs px-2 py-0.5 rounded bg-muted d border border-border font-mono"
                    >
                      {perm}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite User Modal */}
      <Modal
        open={showInvite}
        onClose={() => {
          setShowInvite(false);
          setInviteErrors({});
        }}
        title="Invite User"
        description="Send an invitation to a new platform user"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowInvite(false);
              setInviteErrors({});
            }}
            onSubmit={() => {
              if (validateInviteForm()) {
                setShowInvite(false);
                setInviteForm({
                  name: "",
                  email: "",
                  role: "",
                  accessLevel: "",
                });
                setInviteErrors({});
              }
            }}
            submitLabel="Send Invitation"
            submitDisabled={
              !inviteForm.name || !inviteForm.email || !inviteForm.accessLevel
            }
          />
        }
      >
        <FormField label="Full Name" required error={inviteErrors.name}>
          <FormInput
            placeholder="e.g. Jane Smith"
            value={inviteForm.name}
            onChange={(v) => {
              setInviteForm((f) => ({ ...f, name: v }));
              if (inviteErrors.name)
                setInviteErrors((e) => ({ ...e, name: "" }));
            }}
          />
        </FormField>
        <FormField label="Email Address" required error={inviteErrors.email}>
          <FormInput
            type="email"
            placeholder="e.g. j.smith@org.gov"
            value={inviteForm.email}
            onChange={(v) => {
              setInviteForm((f) => ({ ...f, email: v }));
              if (inviteErrors.email)
                setInviteErrors((e) => ({ ...e, email: "" }));
            }}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Job Role / Title">
            <FormInput
              placeholder="e.g. Security Analyst"
              value={inviteForm.role}
              onChange={(v) => setInviteForm((f) => ({ ...f, role: v }))}
            />
          </FormField>
          <FormField label="Access Level" required>
            <FormSelect
              options={["Admin", "Approver", "Editor", "Assessor", "Viewer"]}
              placeholder="Select access level"
              value={inviteForm.accessLevel}
              onChange={(v) => setInviteForm((f) => ({ ...f, accessLevel: v }))}
            />
          </FormField>
        </div>
        <div className="rounded-md bg-muted/50 border border-border p-3 text-xs d">
          An email invitation will be sent to{" "}
          <strong>{inviteForm.email || "the user"}</strong> with instructions to
          set up their account and MFA.
        </div>
      </Modal>

      {/* New Role Modal */}
      <Modal
        open={showNewRole}
        onClose={() => {
          setShowNewRole(false);
          setRoleErrors({});
        }}
        title="New Role"
        description="Define a new role with a specific set of permissions"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowNewRole(false);
              setRoleErrors({});
            }}
            onSubmit={() => {
              if (validateRoleForm()) {
                setShowNewRole(false);
                setRoleForm({ name: "", description: "", permissions: [] });
                setRoleErrors({});
              }
            }}
            submitLabel="Save"
            submitDisabled={!roleForm.name || roleForm.permissions.length === 0}
          />
        }
      >
        <FormField label="Role Name" required error={roleErrors.name}>
          <FormInput
            placeholder="e.g. Auditor"
            value={roleForm.name}
            onChange={(v) => {
              setRoleForm((f) => ({ ...f, name: v }));
              if (roleErrors.name) setRoleErrors((e) => ({ ...e, name: "" }));
            }}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Describe what this role can do and who it's for"
            value={roleForm.description}
            onChange={(v) => setRoleForm((f) => ({ ...f, description: v }))}
          />
        </FormField>
        <FormField
          label="Permissions"
          required
          hint="Select all permissions this role should have"
        >
          <div className="grid grid-cols-2 gap-2 mt-1">
            {allPermissions.map((perm) => (
              <label
                key={perm}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={roleForm.permissions.includes(perm)}
                  onChange={(e) => {
                    setRoleForm((f) => ({
                      ...f,
                      permissions: e.target.checked
                        ? [...f.permissions, perm]
                        : f.permissions.filter((p) => p !== perm),
                    }));
                  }}
                  className="accent-foreground"
                />
                <span className="text-xs font-mono d">{perm}</span>
              </label>
            ))}
          </div>
        </FormField>
      </Modal>
    </div>
  );
}
